import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import take from 'lodash/take';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';

import {
  COUNTRIES,
  POST_NUMBER_NO_SPACES_LENGTHS,
} from '@cakeiteasy/web-utils';

import { ContextProviderService } from '@shared/services/context-provider.service';
import { KeywordsFetcherService } from '@shared/services/keywords-fetcher.service';
import { AnalyticTracker } from '@analytic/services/analytic-tracker.service';

export interface ILocationSearchItem {
  isCity: boolean;
  label?: string;
  postNumber?: string;
  nothingFound: boolean;
  name: string;
}

@Component({
  standalone: false,
  selector: 'ui-location-search-bar',
  templateUrl: './ui-location-search-bar.component.html',
  styleUrls: ['./ui-location-search-bar.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UiLocationSearchBarComponent implements OnInit {
  @Input() autoFocus: boolean = false;
  @Input() bordless: boolean = true;
  @Input() showLoading: boolean;
  @Input() placeholder: string = '';
  @Input() styleType: string = '';
  @Input() featuredItems: ILocationSearchItem[] = [];
  @Input() showBlinkingCursor = false;
  @Input() showOnlyLocations = false;
  @Input() showOnlyBakeries = false;

  @Output() onLocationFound = new EventEmitter<any>();
  @Output() onSearchBarFocused = new EventEmitter<any>();

  public selectedLocation: any;
  public emptyQuery = true;
  public showDropdown: boolean = true;
  public isFocused: boolean = false;

  constructor(private _keywordsFetcher: KeywordsFetcherService,
              private _contextService: ContextProviderService,
              private _analyticTracker: AnalyticTracker) {
  }

  ngOnInit() {
  }

  public onSelected(item: ILocationSearchItem) {
    if (item.nothingFound === true) {
      return;
    }

    return this.onLocationFound.emit(item);
  }

  public searchLocation(query: string): Observable<ILocationSearchItem[]> {
    const noSpacesQuery = query && query.trim();
    this.emptyQuery = !Boolean(noSpacesQuery);

    if (!query) {
      return of(this.showDropdown ? this.featuredItems || [] : []);
    }

    return this._contextService.countryCode$
      .pipe(switchMap((countryCode: string) => {
        if (!countryCode) {
          return of(this.featuredItems || []);
        }

        // check if string is a post number
        let noSpacesQuery = query.replace(/\s|-/g, '');
        if (isNumber(+noSpacesQuery) && !isNaN(+noSpacesQuery)) {
          if (POST_NUMBER_NO_SPACES_LENGTHS[countryCode] != noSpacesQuery.length) {
            return of(this.featuredItems || []);
          }

          switch (countryCode) {
            case COUNTRIES.NORWAY: {
              query = noSpacesQuery; // xxxx
              break;
            }
            case COUNTRIES.SWEDEN: {
              query = noSpacesQuery.substr(0, 3) + ' ' + noSpacesQuery.substr(-2); // xxx xx
              break;
            }
            default: {
              return of(this.featuredItems || []);
            }
          }
        }

        let nothingFoundItem: ILocationSearchItem = {
          name: 'Nothing found...',
          nothingFound: true,
          isCity: false
        };

        return this._keywordsFetcher.getStoreKeywords(countryCode, query)
          .pipe(
            map((results) => {
              if (this.showOnlyLocations) {
                results = results.filter((item) => item.isCity);
              }

              if (this.showOnlyBakeries) {
                results = results.filter((item) => !item.isCity);
              }

              const payload = { searchTerm: query, pageLocation: window.location.href };

              if (results.length > 0) {
                this._analyticTracker.trackSearch(payload);
                return take(results, 7);
              }

              this._analyticTracker.trackSearchNoResult(payload);
              return [nothingFoundItem];
            }),
            catchError(error => {
              return of([nothingFoundItem]);
            })
          );
      }));
  }

  public setFocused() {
    setTimeout(() => this.isFocused = true);
  }
}
